import type { CollectionCardConfig } from '@/lib/blocks/collection/blocks';
import {
  buildCardStyle,
  getCollectionCardPaddingClassName,
  resolveSymmetricAlternatingGap,
  resolveSymmetricGridLayout,
  resolveSymmetricPadding,
} from '@/lib/blocks/collection/blocks';
import {
  applyCollectionButtonSettingsToItems,
  readCollectionButtonSettings,
} from '@/lib/blocks/collection/button-settings';
import {
  type DataWidgetBlockType,
  filterDataWidgetFieldsForSourceMode,
  getDataWidgetVariantEntry,
  getDataWidgetVariantProps,
  hasActiveDataWidgetFilters,
  isDataWidgetBlockType,
  resolveDataWidgetCarouselDisplay,
  resolveDataWidgetProps,
} from '@/lib/blocks/collection/data-widget/blocks';
import { enrichBlogCollectionItem } from '@/lib/blocks/collection/item-mappers/blog';
import { parseCollectionMediaLayout } from '@/lib/blocks/collection/media-layout';
import type { PageBlock } from '@/types/block';
import {
  AccordionCollectionRenderer,
  AlternatingRenderer,
  CollectionCardFieldRenderer,
  CollectionCardRenderer,
  CollectionCarouselRenderer,
  CollectionGridRenderer,
} from './collection-card-renderer';
import { DataWidgetEmptyState } from './data-widget-empty-state';
import { buildRenderFieldForItem } from './data-widget-render-fields';

function enrichWidgetItems(
  blockType: DataWidgetBlockType,
  items: ReturnType<typeof resolveDataWidgetProps>['items'],
) {
  if (blockType !== 'blog_widget') return items;
  return items.map(enrichBlogCollectionItem);
}

function resolveRenderableCardConfig(
  block: PageBlock,
  cardConfig: CollectionCardConfig,
  sourceMode: 'dynamic' | 'static',
): CollectionCardConfig {
  if (!isDataWidgetBlockType(block.blockType)) {
    return cardConfig;
  }

  return {
    ...cardConfig,
    fields: filterDataWidgetFieldsForSourceMode(block.blockType, cardConfig.fields, sourceMode),
    sourceMode,
  };
}

export function DataWidgetRenderer({
  block,
  cardConfig,
}: {
  block: PageBlock;
  cardConfig: CollectionCardConfig;
}) {
  const props = resolveDataWidgetProps(block);
  const rawItems = props.source.mode === 'dynamic' ? props.items : props.staticItems;
  const enrichedItems = enrichWidgetItems(block.blockType as DataWidgetBlockType, rawItems);
  const items =
    props.source.mode === 'dynamic'
      ? applyCollectionButtonSettingsToItems(
          enrichedItems,
          cardConfig,
          readCollectionButtonSettings(block.props as Record<string, unknown>),
        )
      : enrichedItems;
  if (cardConfig.fields.length === 0) return null;
  if (items.length === 0) {
    if (props.source.mode !== 'dynamic') return null;
    const hasDynamicRecords = Boolean(block.props?.hasDynamicRecords);
    return (
      <DataWidgetEmptyState
        blockType={block.blockType as DataWidgetBlockType}
        strategy={props.source.strategy}
        hasDynamicRecords={hasDynamicRecords}
        hasActiveFilters={hasActiveDataWidgetFilters(props.source.filters)}
      />
    );
  }
  const renderableCardConfig = resolveRenderableCardConfig(block, cardConfig, props.source.mode);

  const variantEntry = getDataWidgetVariantEntry(props.variant);
  const variantProps = getDataWidgetVariantProps(props);
  const paddingClassName = getCollectionCardPaddingClassName(resolveSymmetricPadding(variantProps));
  const mediaLayout = parseCollectionMediaLayout(variantProps);
  const { style: cardStyle, hasCardStyling } = buildCardStyle(variantProps);

  if (variantEntry.renderer === 'alternating') {
    const alternatingGap = resolveSymmetricAlternatingGap(variantProps);

    return (
      <AlternatingRenderer
        items={items}
        cardConfig={renderableCardConfig}
        alternatingGap={alternatingGap}
        paddingClassName={paddingClassName}
        cardStyle={cardStyle}
        hasCardStyling={hasCardStyling}
        renderCard={(item, index) => (
          <CollectionCardRenderer
            item={item}
            cardConfig={renderableCardConfig}
            mediaLayout={{
              ...mediaLayout,
              position: index % 2 === 0 ? 'right' : 'left',
            }}
            sideBySideMode="alternating"
            paddingClassName={paddingClassName}
            renderField={buildRenderFieldForItem(block, item, renderableCardConfig)}
          />
        )}
      />
    );
  }

  if (variantEntry.renderer === 'accordion') {
    const accordionLayout = resolveSymmetricGridLayout(variantProps);
    const { style: containerStyle, hasCardStyling } = buildCardStyle(variantProps);
    return (
      <AccordionCollectionRenderer
        items={items}
        cardConfig={renderableCardConfig}
        padding={resolveSymmetricPadding(variantProps)}
        containerStyle={containerStyle}
        hasCardStyling={hasCardStyling}
        expandFirstItem={Boolean(variantProps.expandFirstItem)}
        allowMultiple={variantProps.allowMultiple !== false}
        showDividers={variantProps.showDividers !== false}
        iconStyle={variantProps.iconStyle === 'chevron' ? 'chevron' : 'plus'}
        layout={accordionLayout}
        questionSlot="title"
        renderBodyField={(item) =>
          buildRenderFieldForItem(block, item, renderableCardConfig) ??
          ((field, value) => (
            <CollectionCardFieldRenderer
              field={field}
              value={value}
              align={renderableCardConfig.align}
            />
          ))
        }
      />
    );
  }

  if (variantEntry.renderer === 'carousel') {
    const display = resolveDataWidgetCarouselDisplay(props);

    return (
      <CollectionCarouselRenderer
        items={items}
        display={display}
        cardConfig={renderableCardConfig}
        mediaLayout={mediaLayout}
        paddingClassName={paddingClassName}
        cardStyle={cardStyle}
        hasCardStyling={hasCardStyling}
        renderFieldForItem={(item) => buildRenderFieldForItem(block, item, renderableCardConfig)}
      />
    );
  }

  const imageAsBg = mediaLayout.position === 'background';

  return (
    <CollectionGridRenderer
      items={items}
      cardConfig={renderableCardConfig}
      layout={resolveSymmetricGridLayout(variantProps)}
      paddingClassName={imageAsBg ? '' : paddingClassName}
      cardStyle={imageAsBg ? undefined : cardStyle}
      hasCardStyling={imageAsBg ? false : hasCardStyling}
      renderCard={(item) => (
        <CollectionCardRenderer
          item={item}
          cardConfig={renderableCardConfig}
          mediaLayout={mediaLayout}
          renderField={buildRenderFieldForItem(block, item, renderableCardConfig)}
        />
      )}
    />
  );
}
